import { Modal } from '@mantine/core';
import { IconCloudDownload } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import classes from '../compliance/Compliance.module.css';
import { pageKind } from '../compliance/PageViewer.js';
import { FileViewer } from '../documents/FileViewer.js';
import type { CrewFileRecord } from './types.js';

/** Yüklenebilen türler (ADR 0164; sunucu içerikten de denetler). */
export const CREW_FILE_ACCEPT = 'application/pdf,image/jpeg,image/png';
const CREW_FILE_ERRORS = new Set(['FILE_TOO_LARGE', 'FILE_TYPE_MISMATCH', 'FORBIDDEN']);

/** Yükleme hatasının çeviri anahtarı: bilinen kod kendi metniyle, öteki genel metinle. */
export function crewFileErrorKey(code: string): string {
  return CREW_FILE_ERRORS.has(code) ? `crewFiles.errors.${code}` : 'crewFiles.uploadFailed';
}

export interface CrewFileViewerProps {
  file: CrewFileRecord | null;
  /** `Range` destekli adres (web: ofis ucu; gemi: `felix-file://`); açılış sunucuda kayda geçer. */
  url: (fileId: string) => string;
  onClose: () => void;
}

/**
 * Personel sertifikası dosyası ya da kontrat belgesinin önizleyicisi (ADR 0164): PDF ya da
 * görüntü, uygulama içinde açılır (gemide pencere açılmaz). Gemide inmemiş dosya "Not on board"
 * (K34); istek gönderilmez, kayda da geçmez.
 */
export function CrewFileViewer({ file, url, onClose }: CrewFileViewerProps) {
  const { t } = useTranslation();
  return (
    <Modal
      opened={file !== null}
      onClose={onClose}
      size="min(1100px, 96vw)"
      title={<span className={classes.drawerTitle}>{file?.name ?? ''}</span>}
      closeButtonProps={{ 'aria-label': t('crewFiles.close') }}
    >
      {file ? (
        <div className={classes.viewer}>
          {file.onBoard === false ? (
            <EmptyState
              icon={<IconCloudDownload size={22} />}
              title={t('crewFiles.notOnBoard')}
              description={t('crewFiles.notOnBoardHelp')}
            />
          ) : (
            <FileViewer
              key={file.id}
              file={{
                id: file.id,
                name: file.name,
                size: file.size,
                kind: pageKind({ mimeType: file.mimeType, fileName: file.name }),
              }}
              url={url(file.id)}
              state="published"
              canDownload={false}
              canPrint={false}
              onDownload={() => undefined}
              onPrint={() => undefined}
            />
          )}
        </div>
      ) : null}
    </Modal>
  );
}
